/**
 * auth-surface.ts — the ONE canonical read of the shared Supabase auth surface.
 *
 * Epic: docs/epics/active/cross-spa-auth-propagation-260705/main.md Phase 2.
 *
 * WHY THIS MODULE EXISTS (the bug it kills):
 *   Before this module, each separately-bundled SPA (main `spa`, `spa-account`,
 *   `spa-history`) hand-wrote its own "resolve the auth surface off `window`"
 *   logic. When the `window-adatool-ns` migration moved the
 *   surface from the retired flat global `window.AdaSupabase` to the namespaced
 *   `window.adatool.supabase`, it updated the main SPA + inline HTML scripts but
 *   MISSED the two sub-SPA `init.ts` bundles. They kept reading the (now
 *   `undefined`) `window.AdaSupabase`, so their `authFetch` silently fell back to
 *   a bare `fetch` with NO `Authorization: Bearer` header. Supabase OAuth
 *   sessions are bearer-only (a JWT in localStorage; NO server-readable cookie),
 *   so the account/history API calls 401'd and the sub-SPAs rendered signed-out
 *   while the main SPA was signed in. RCA: `phase-1-260705.md` §C.
 *
 * THE FIX (A.6 / A.19 — one canonical, type-checked seam; fix-once-benefits-all):
 *   This is the SINGLE place any SPA reaches `window.adatool.supabase`. All three
 *   SPAs import from here. A read-side ban-lint
 *   (`lint-no-retired-adasupabase-read.py`) forbids (a) any read of the retired
 *   `window.AdaSupabase` global and (b) any direct reach into
 *   `window.adatool.supabase` from OUTSIDE this module — so a future rename fixes
 *   ONE site and can never again leave a bundle behind.
 *
 * FAIL-LOUD, NOT SILENT-DEGRADE (A.14 / RCA §F "silent-degradation dynamic"):
 *   The old `_win.AdaSupabase ? … : fetch(…)` ternary turned "auth surface
 *   missing" into a *silent* unauthenticated request. Here, when the surface is
 *   absent, `getAuthFetch()` logs a HIGH-severity warning before returning the
 *   bare-`fetch` fallback — so a future regression surfaces as a logged error,
 *   not an invisible signed-out screen.
 *
 * Load order: `supabase-client.js` (which assigns `window.adatool.supabase`)
 * loads BEFORE the SPA `init.js` on every page (blocking scripts on
 * account.html / history.html; deferred + source-ordered on index.html). So by
 * the time an SPA calls these helpers, the surface is present in the normal case.
 *
 * Source: web/static/spa-shared/auth-surface.ts — compiled to a `.js` sibling by
 * the spa-account / spa-history tsc emit passes (tools/build_all_ts.py); the
 * emitted `.js` is gitignored (rule #24).
 */
function _win() {
    return window;
}
function _log() {
    return _win().adatool?.log ?? console;
}
/**
 * The ONE read of `window.adatool.supabase`. Returns the surface, or `null` if
 * it has not been installed yet (should not happen in the normal load order —
 * see the module header). Callers get a typed handle; nobody else reaches into
 * `window.adatool.supabase` directly.
 */
export function getAuthSurface() {
    return _win().adatool?.supabase ?? null;
}
/**
 * Canonical `authFetch`: ALWAYS attaches the Bearer from the shared surface.
 *
 * Returns a bound `authFetch` when the surface is present. When it is ABSENT,
 * logs a HIGH-severity warning and returns a bare `fetch` fallback — the request
 * will be unauthenticated, but the failure is now LOUD (logged), not silent.
 * This is the exact anti-pattern that caused the cross-SPA bug: the old code
 * fell back to bare `fetch` with no signal at all.
 */
export function getAuthFetch() {
    const surface = getAuthSurface();
    if (surface) {
        return (url, opts) => surface.authFetch(url, opts);
    }
    _log().warn('[auth-surface] window.adatool.supabase is UNAVAILABLE — falling back to '
        + 'bare fetch (NO Authorization: Bearer). Authenticated requests will 401. '
        + 'This means the shared auth surface (supabase-client.js) did not load '
        + 'before this SPA bundle. See spa-shared/auth-surface.ts.');
    return (url, opts) => fetch(url, opts);
}
/** Read the current session access token via the shared surface (or null). */
export async function getAccessToken() {
    const surface = getAuthSurface();
    if (!surface) {
        _log().warn('[auth-surface] getAccessToken: window.adatool.supabase unavailable → null');
        return null;
    }
    return surface.getAccessToken();
}
/** Write a one-shot handoff token via the shared surface (no-op if absent). */
export function setHandoffToken(token) {
    const surface = getAuthSurface();
    if (!surface) {
        _log().warn('[auth-surface] setHandoffToken: window.adatool.supabase unavailable → skipped');
        return false;
    }
    return surface.setHandoffToken(token);
}
